import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  CisContainer,
  Row,
  Col,
  ButtonSearch,
  Pagination,
  Spinner,
  Modal,
  Table,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { fetchPdfCisReports } from '../../../api/services/cis';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import checkPermissions from '../../../helpers/checkPermissions';

const CvmbReports = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeReport,
  setProfile,
  countries,
  loadCountries,
  userPermissions,
}) => {
  const { items, page, pages, perPage, sort, totalItems, filters } = list;

  const { t } = useTranslation();
  const history = useHistory();

  const isCvmbCis = history.location?.pathname?.includes('cvmb-cis') ?? false;
  const manageCisPermission =
    (isCvmbCis && checkPermissions({ roles: ['ipma_cis_cvmb'] }, userPermissions)) ||
    (!isCvmbCis && checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions));

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    setPage(1);
    setPerPage(10);
    setSort('year.desc');
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.year || ''}
                  onFilter={(val) => handleColumnFilterChange('year', val)}
                  filterPlaceholder={t('page.user.cis.certificationReports.yearPlaceholder')}
                  label={t('page.user.cis.certificationReports.year')}
                  sort
                  sortDirection={getSortDirection('year')}
                  onSort={(dir) => handleColumnSort('year', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.nameOfCb || ''}
                  onFilter={(val) => handleColumnFilterChange('nameOfCb', val)}
                  filterPlaceholder={t('page.user.cis.certificationReports.nameOfCB')}
                  label={t('page.user.cis.certificationReports.nameOfCB')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.statusManual || ''}
                  onFilter={(val) => handleColumnFilterChange('statusManual', val)}
                  filterOptions={[
                    { label: 'Submitted', value: 'Submitted' },
                    { label: 'Accepted', value: 'Accepted' },
                  ]}
                  filterPlaceholder={t('page.user.cis.certificationReports.statusPlaceholder')}
                  label={t('page.user.cis.certificationReports.status')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFrom || ''}
                  filterValueTo={filters.dateTo || ''}
                  filterValueYear={filters.dateYear || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateTo', val)}
                  onFilterYear={(val) => handleColumnFilterChange('dateYear', val)}
                  filterPlaceholder={t('page.user.cis.certificationReports.datePlaceholder')}
                  label={t('page.user.cis.certificationReports.date')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.country || ''}
                  onFilter={(val) => handleColumnFilterChange('country', val)}
                  filterOptions={countries}
                  filterPlaceholder={t('page.user.cis.certificationReports.country')}
                  label={t('page.user.cis.certificationReports.country')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                {t('page.user.cis.certificationReports.exportToPdf')}
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                {manageCisPermission
                  ? t('page.user.cis.certificationReports.viewEdit')
                  : t('page.user.cis.certificationReports.view')}
              </CisTable.StickyHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={7}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={7}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.year')}</Table.Label>
                      {item.year}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.nameOfCB')}</Table.Label>
                      {item.certificationBody?.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.status')}</Table.Label>
                      {item.status}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.date')}</Table.Label>
                      {item.dateOfReport ? moment(item.dateOfReport).format('DD.MM.YYYY') : '-'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.country')}</Table.Label>
                      {item.certificationBody?.country?.name || '-'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificationReports.exportToPdf')}
                      </Table.Label>
                      <ButtonSearch
                        icon="exportToPdf"
                        color="black"
                        secondary
                        rounded
                        onClick={() => {
                          fetchPdfCisReports(item.id);
                        }}
                        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                      />
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {manageCisPermission
                          ? t('page.user.cis.certificationReports.viewEdit')
                          : t('page.user.cis.certificationReports.view')}
                      </Table.Label>
                      {!manageCisPermission && (
                        <ButtonSearch
                          icon="searchIcon"
                          secondary
                          rounded
                          onClick={() => {
                            setProfile(item);
                            history.push('/ipma/cvmb-cis/reports/view');
                          }}
                          style={{
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            marginRight: '5px',
                          }}
                        />
                      )}
                      {manageCisPermission && item.status === 'Accepted' && (
                        <>
                          <ButtonSearch
                            icon="searchIcon"
                            secondary
                            rounded
                            onClick={() => {
                              setProfile(item);
                              history.push('/ipma/cvmb-cis/reports/view');
                            }}
                            style={{
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              marginRight: '5px',
                            }}
                          />
                          <ButtonSearch
                            icon="trash"
                            color="black"
                            secondary
                            rounded
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            onClick={() => removeReport(item.id)}
                          />
                        </>
                      )}
                      {manageCisPermission &&
                        (item.status === 'Draft' || item.status === 'Submitted') && (
                          <ButtonSearch
                            icon="edit"
                            color="black"
                            onClick={() => {
                              setProfile(item);
                              history.push('/ipma/cvmb-cis/reports/edit');
                            }}
                            secondary
                            rounded
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                          />
                        )}
                    </CisTable.Cell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </CisContainer>
    </PageContent>
  );
};

CvmbReports.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['ipma', 'ma']),
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  removeReport: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
};

CvmbReports.defaultProps = {
  loading: false,
  type: 'ipma',
  list: {},
};

export default CvmbReports;
